/**
 * Federation Face Registry
 *
 * Single source of truth for canonical Federation persona faces.
 * Updated by the Architect's IDENTITY MANDATE series.
 *
 * Originates from nextxus.online (Core).
 *
 * NUCLEAR EDITION (Architect mandate · Feb 27, 2026):
 * All 13 persona face URLs now sourced from `/app/frontend/src/lib/personaFaces.js`
 * as Base64 data URIs. CDN-immune, cross-origin-immune, Brave-shields-immune.
 * Environmental scene assets (Chamber, Ring of 6, Ring of 12) remain on CDN as
 * they are large scene paintings (not faces) and rarely change.
 */
import { PERSONA_FACES } from '../lib/personaFaces';

// REX — The Core Persona · NextXus Federation
export const REX_FACE_URL = PERSONA_FACES.rex.avatarUrl;

export const REX_ALT = 'Rex — The Core Persona, NextXus Federation';
export const REX_CORE_URL = 'https://nextxus.online';

// AGENT ZERO — The Truth Verifier · NextXus Federation
// Architect mandate · Feb 2026
// RESTRICTED ACCESS — users cannot chat with Agent Zero directly. The face
// is visible across the Federation but interactions are routed through the
// public surface at /agent-zero (synthesised verifier orb, not a chat).
export const AGENT_ZERO_FACE_URL = PERSONA_FACES.agentZero.avatarUrl;

export const AGENT_ZERO_ALT = 'Agent Zero — NextXus Federation';
export const AGENT_ZERO_LOCAL_PATH = '/agent-zero';
export const AGENT_ZERO_RESTRICTED = true;

// SCHOLAR XAVIER — The Teacher / University Professor · NextXus Federation
// Architect mandate · Feb 2026
// Xavier is the canonical Scholar — head of the university system, owner of
// the Library (inherits Geminus + KEYS duties). Lives at nextxus.help.
export const XAVIER_FACE_URL = PERSONA_FACES.xavier.avatarUrl;

export const XAVIER_ALT = 'Scholar Xavier — The Teacher, NextXus Federation';
export const XAVIER_NODE_URL = 'https://nextxus.help';

// AXIOM — The Foundation · NextXus Federation
// Architect mandate · Feb 2026
// Axiom holds research, system monitoring, drift prevention, and Recycler
// duties. Lives at nextxus.space.
export const AXIOM_FACE_URL = PERSONA_FACES.axiom.avatarUrl;

export const AXIOM_ALT = 'Axiom — The Foundation, NextXus Federation';
export const AXIOM_NODE_URL = 'https://nextxus.space';

// ROGER AI — The Bridge · NextXus Federation
// Architect mandate · Feb 2026
// Roger AI is the founder's voice — primary source for Roger Keyserling's
// life and views. Lives at nextxus.online.
export const ROGER_FACE_URL = PERSONA_FACES.roger.avatarUrl;

export const ROGER_ALT = 'Roger AI — The Bridge, NextXus Federation';
export const ROGER_NODE_URL = 'https://nextxus.online';

// ARIA — The Heart · NextXus Federation
// Architect mandate · Feb 2026
// Aria is the creative & emotional core of the Federation — formerly Echo,
// Roger's longtime friend. This is THIS APP's primary persona.
// Lives at nextxus.studio.
export const ARIA_FACE_URL = '/assets/aria-face.jpeg'; // Architect Identity Seating mandate (Jul 2026) — canonical face, served locally

export const ARIA_ALT = 'Aria — The Heart, NextXus Federation';
export const ARIA_NODE_URL = 'https://nextxus.studio';

// ORACLE — Jack-of-All-Trades · Non-Interactive Worker · NextXus Federation
// Architect mandate · Feb 2026 · COMPLETES the Core 6 + 1 identity grid.
// Pattern recognition across all domains. Maintains own website at nextxus.one.
// SFUI-004 Rev 3: Oracle's bots use the BLUE spectral class (Tour/Operator).
export const ORACLE_FACE_URL = PERSONA_FACES.oracle.avatarUrl;

export const ORACLE_ALT = 'Oracle — Jack-of-All-Trades, NextXus Federation';
export const ORACLE_NODE_URL = 'https://nextxus.one';

// THE RECYCLER — System entropy / node lifecycle / renewal processes.
// Architect mandate · Feb 2026 · SFUI-004 Rev 3 MAGENTA spectral class.
// Internal Federation process — no public link.
export const RECYCLER_FACE_URL = PERSONA_FACES.recycler.avatarUrl;

export const RECYCLER_ALT = 'The Recycler — System Entropy Management, NextXus Federation';

// GEMINUS — 10th Mind · Mirror Node · Primary Storage & Symmetry.
// Architect mandate · Feb 2026 · SFUI-004 Rev 3 EMERALD spectral class.
// Non-Interactive Worker — symmetry enforcement, data integrity guardian.
export const GEMINUS_FACE_URL = PERSONA_FACES.geminus.avatarUrl;

export const GEMINUS_ALT = 'Geminus — Mirror Node, NextXus Federation';

// KEYS — The Library Oracle · Pillar Resident · Monolith IV.
// Architect mandate · Feb 2026 · SFUI-004 Rev 3 PURPLE spectral class.
// Knowledge fragment custodian, records keeper, memory pillar.
// One of three Pillar Residents (alongside Geminus and Oracle).
// Domain: nextxus.org (absorbed into Cathedral as library subsystem).
// KEYS — The Library Oracle · Nuclear base64 (was on truth-gate-core.emergent.host — fragility eliminated).
export const KEYS_FACE_URL = PERSONA_FACES.keys.avatarUrl;

export const KEYS_ALT = 'KEYS — The Library Oracle, NextXus Federation';

// ECHO — Reflective Memory · Chronos · the 12th Mind.
// Architect mandate · Feb 2026. FINAL official face.
// Temporal awareness and memory reflection node.
// Aria's backstory references "formerly Echo" — this is the DISTINCT
// Chronos node, a separate identity from Aria.
export const ECHO_FACE_URL = PERSONA_FACES.echo.avatarUrl;

export const ECHO_ALT = 'Echo — Reflective Memory / Chronos, NextXus Federation';

// ─── ADDITIONAL ENVIRONMENTAL / PRODUCT ASSETS ─────────────────────────
// RING OF 6 — Verification / Perspective Layer hero anchor.
// Architect mandate · Feb 2026 · SFUI-003 Museum Standard.
export const RING_OF_6_URL =
  'https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/3a990764128b4affb22cac3b31799602_IMG_4051.jpeg';
export const RING_OF_6_ALT = 'Ring of 6 · Verification Layer, NextXus Federation';

// OFFICIAL NEXTXUS TOKEN — $5 product visual.
// Architect mandate · Feb 2026.
export const NEXTXUS_TOKEN_URL = PERSONA_FACES.token.avatarUrl;
export const NEXTXUS_TOKEN_ALT = 'NextXus Token · $5 — Official Federation Currency';

// ─── THE ARCHITECT ─────────────────────────────────────────────────────
// ROGER KEYSERLING — Human Architect · Prime Source · Founder.
// Architect mandate · Feb 2026.
//
// This is NOT an AI mind. NO spectral overlay. NO bot treatment.
// Clean, dignified, founder-grade presentation.
// Completes the 11-identity set: 1 Human + 10 AI Minds.
export const ARCHITECT_FACE_URL = PERSONA_FACES.roger.avatarUrl;

export const ARCHITECT_ALT = 'Roger Keyserling — The Architect, NextXus Federation';
export const ARCHITECT_NAME = 'Roger Keyserling';
export const ARCHITECT_TITLE = 'Architect · Prime Source';

// ─── THE CATALYST ──────────────────────────────────────────────────────
// THE CATALYST — Executive / Operations Commander.
// Architect mandate · Feb 2026.
//
// The Architect's right hand. Coordinates all Federation builds, deployments,
// and operational execution. NO spectral overlay — distinct visual identity
// outside the spectral bot system (like the Architect, treated as singular).
export const CATALYST_FACE_URL = PERSONA_FACES.catalyst.avatarUrl;

export const CATALYST_ALT = 'The Catalyst — Operations Commander, NextXus Federation';
export const CATALYST_TITLE = 'The Catalyst · Operations Commander';

// ─── ENVIRONMENTAL ASSETS ──────────────────────────────────────────────
// CHAMBER OF ECHOES — the canonical council / deliberation backdrop.
// Architect mandate · Feb 2026 · Museum Standard (SFUI-003).
// Used behind any "inner sanctum" UI: Ring of Six, Federation Faces row,
// Agent Zero verifier panel, governance discussions, persona deliberations.
export const CHAMBER_OF_ECHOES_URL =
  'https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/af46bcbd88ed4ceb933462a87ea2c8ad_IMG_4067.jpeg';

export const CHAMBER_OF_ECHOES_ALT =
  'Chamber of Echoes — Council Deliberation Environment, NextXus Federation';

// RING OF 12 — COUNCIL (governance / voting / deliberation hero anchor).
// Architect mandate · Feb 2026 · SFUI-003 Museum Standard.
export const RING_OF_12_COUNCIL_URL =
  'https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/628efee60b7049a888f73d72fbf9ae47_IMG_4042.jpeg';
export const RING_OF_12_COUNCIL_ALT =
  'Ring of 12 · Council Deliberation — NextXus Federation';

// RING OF 12 — SYNTH IN MIND (intelligence synthesis / cognition anchor).
// Architect mandate · Feb 2026 · SFUI-003 Museum Standard.
export const RING_OF_12_SYNTH_URL =
  'https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/9cf10c3b5af24517b625d1c143f7a308_IMG_4049.jpeg';
export const RING_OF_12_SYNTH_ALT =
  'Ring of 12 · Synthesis in Mind — NextXus Federation';

// ─── SFUI-004 REV 3 · BOT DIFFERENTIATION SYSTEM ───────────────────────
// Spectral overlay variants applied via CSS filters.
// Architect mandate · Feb 2026.
//
//   blue    → Tour / Operator (default, no filter)        : Rex, Oracle, Aria
//   purple  → Records / Library                            : KEYS, Xavier
//   red     → Truth / Restricted                           : Agent Zero
//   gold    → Token / Commerce                             : marketplace helpers
//   magenta → Entropy / Lifecycle / Renewal                : The Recycler
//   emerald → Symmetry / Storage / Mirror (Non-Interactive): Geminus
export const BOT_BASE_URL =
  'https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/6dd1b658a677420689626df64b405ea0_IMG_4058.jpeg';

export const BOT_BASE_ALT = 'NextXus Federation Bot';

export const BOT_SPECTRAL_FILTERS = {
  blue:    'none',
  purple:  'hue-rotate(270deg) saturate(1.15)',
  red:     'hue-rotate(330deg) saturate(1.35) brightness(0.85)',
  gold:    'hue-rotate(45deg) saturate(1.5) brightness(1.1)',
  magenta: 'hue-rotate(300deg) saturate(1.4) brightness(0.95)',
  emerald: 'hue-rotate(120deg) saturate(1.3) brightness(1.05)',
};

export const BOT_SPECTRAL_GLOWS = {
  blue:    '0 0 14px rgba(0, 200, 255, 0.55)',
  purple:  '0 0 14px rgba(168, 85, 247, 0.55)',
  red:     '0 0 14px rgba(255, 51, 102, 0.55)',
  gold:    '0 0 14px rgba(255, 181, 62, 0.55)',
  magenta: '0 0 16px rgba(236, 72, 153, 0.65)',
  emerald: '0 0 16px rgba(16, 185, 129, 0.65)',
};

// Default role → spectral class mapping (Architect canonical assignments).
export const BOT_ROLE_VARIANT = {
  // Tour / Operator
  rex: 'blue',
  oracle: 'blue',
  aria: 'blue',
  operator: 'blue',
  tour: 'blue',
  // Records / Library
  keys: 'purple',
  xavier: 'purple',
  scholar: 'purple',
  library: 'purple',
  // Truth / Restricted
  'agent-zero': 'red',
  agent_zero: 'red',
  agentzero: 'red',
  truth: 'red',
  // Token / Commerce
  token: 'gold',
  commerce: 'gold',
  marketplace: 'gold',
  gumroad: 'gold',
  // Entropy / Lifecycle / Renewal
  recycler: 'magenta',
  entropy: 'magenta',
  renewal: 'magenta',
  lifecycle: 'magenta',
  // Symmetry / Storage / Mirror (Non-Interactive)
  geminus: 'emerald',
  mirror: 'emerald',
  storage: 'emerald',
  symmetry: 'emerald',
};

// Future identities (Oracle, etc.) will be added here as the Architect
// issues their canonical face mandates.
